/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { IconProps } from '../../types';
import { COLORED_ICONS, ICON_REGISTRY } from './registry';
import './Icon.less';

const Icon = ({
  name,
  size = 'medium',
  variant = 'default',
  className,
  'aria-label': ariaLabel,
  role,
  style,
}: IconProps) => {
  const SvgComponent = ICON_REGISTRY[name];
  if (!SvgComponent) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[DS Icon] No icon registered for name "${name}". Add it to src/design-system/assets/icons/ and re-run the registry generator.`
      );
    }
    return null;
  }

  // Brand / multi-color icons ship with embedded fills — skip the variant
  // class so we don't recolor them.
  const applyVariant = !COLORED_ICONS.has(name);

  const isLabelled = !!ariaLabel;
  const ariaProps = isLabelled
    ? { role: role ?? 'img', 'aria-label': ariaLabel }
    : { role: role ?? 'presentation', 'aria-hidden': true as const };

  return (
    <SvgComponent
      className={cx(
        'icon',
        `icon--${name.replace('/', '-')}`,
        `icon--size-${size}`,
        applyVariant && `icon--variant-${variant}`,
        className
      )}
      style={style}
      {...ariaProps}
    />
  );
};

Icon.displayName = 'Icon';

export default Icon;
